import {DrivingInfoTripContentProps} from '@di-shared/features/history/driving-info/DrivingInfoTripContent/index';
import {ErrorBoundary} from 'react-error-boundary';
import React, {Suspense} from 'react';
import useTripDetailKr from '@di-shared/features/history/date/useTripDetail.kr';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {isSameDate, parseDateWithFormat} from '@shared/dateUtils';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {formatNumberWithCommas} from '@di-shared/utils';
import {ERROR} from '@di-shared/const';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';

export default function ({vin, selectedDate}: DrivingInfoTripContentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate}: DrivingInfoTripContentProps) {
  const {isEmptyTrip} = useTripDetailKr({vin, selectedDate});

  // No data 화면
  if (isEmptyTrip) {
    return <None selectedDate={selectedDate} />;
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function None({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('T10008')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('T10067')}</span>
        </div>
      </div>
    </>
  );
}

function Default({vin, selectedDate}: DrivingInfoTripContentProps) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol} = useUnitConverter();
  const {checkAvailableComponent} = useAvailableComponentKr({vin});
  const {summaryData, totalDrivingHours, totalDrivingMinutes, formattedRecentTripDetail} = useTripDetailKr({
    vin,
    selectedDate,
  });

  const ignitionOnDate = parseDateWithFormat(formattedRecentTripDetail.ignOnTime);
  const ignitionOffDate = parseDateWithFormat(formattedRecentTripDetail.ignOffTime);

  if (!ignitionOnDate || !ignitionOffDate) {
    throw new Error('ignitionOnDate or ignitionOffDate error');
  }

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('T10008')}</span>}
        </div>
        <div
          className="trip_summary_list type_multi_line"
          /**
           * [2025-09-24:Jeyoung Lee] FIXME: 임시 인라인 스타일
           */
          style={{justifyContent: 'space-around'}}
        >
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div
              className="trip_summary_info"
              /**
               * [2025-09-24:Jeyoung Lee] FIXME: 임시 인라인 스타일
               */
              style={{margin: 'auto'}}
            >
              <span className="trip_summary_value">{formatNumberWithCommas(convertDistanceValue(summaryData.totalDrvDist))}</span>
              <span className="trip_summary_unit">{convertDistanceSymbol(summaryData.totalDrvDist)}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div
              className="trip_summary_info"
              /**
               * [2025-09-24:Jeyoung Lee] FIXME: 임시 인라인 스타일
               */
              style={{margin: 'auto'}}
            >
              {!!totalDrivingHours && (
                <>
                  <span className="trip_summary_value">{totalDrivingHours}</span>
                  <span className="trip_summary_unit">{t('T10054')}</span>
                </>
              )}
              {!!totalDrivingMinutes && (
                <>
                  <span className="trip_summary_value">{totalDrivingMinutes}</span>
                  <span className="trip_summary_unit">{t('T10055')}</span>
                </>
              )}
              {!totalDrivingHours && !totalDrivingMinutes && <span className="trip_summary_value">{t('T10202')}</span>}
            </div>
          </div>
          {checkAvailableComponent(PermissionTypeKr.SAFETY_SCORE) && (
            <>
              <div className="divider divider_vertical" />
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('T10057')}</span>
                <div
                  className="trip_summary_info"
                  /**
                   * [2025-09-24:Jeyoung Lee] FIXME: 임시 인라인 스타일
                   */
                  style={{margin: 'auto'}}
                >
                  <span className="trip_summary_value">{formatNumberWithCommas(summaryData.safetyDrvScore)}</span>
                  <span className="trip_summary_unit">{t('T10056')}</span>
                  {summaryData.safetyDrvScoreDiff >= 0 ? (
                    <span className="trip_summary_compare type_up">
                      <span
                        className="ic ic_di_count_up"
                        role="img"
                        aria-label={t('T10059')}
                      />
                      <span className="trip_summary_value">{formatNumberWithCommas(Math.abs(summaryData.safetyDrvScoreDiff))}</span>
                    </span>
                  ) : (
                    <span className="trip_summary_compare type_down">
                      <span
                        className="ic ic_di_count_down"
                        role="img"
                        aria-label={t('T10058')}
                      />
                      <span className="trip_summary_value">{formatNumberWithCommas(Math.abs(summaryData.safetyDrvScoreDiff))}</span>
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="trip_info_head">
          <span className="trip_info_item">
            {DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}
            &nbsp;(
            {!formattedRecentTripDetail.intervalIgnitionHours && !formattedRecentTripDetail.intervalIgnitionMinutes && t('T10202')}
            {!!formattedRecentTripDetail.intervalIgnitionHours &&
              t('T30103_$hour', {hour: `${formattedRecentTripDetail.intervalIgnitionHours}`})}
            {!!formattedRecentTripDetail.intervalIgnitionHours && !!formattedRecentTripDetail.intervalIgnitionMinutes && <>&nbsp;</>}
            {!!formattedRecentTripDetail.intervalIgnitionMinutes &&
              t('T30104_$minute', {minute: formattedRecentTripDetail.intervalIgnitionMinutes})}
            )
          </span>
          {formattedRecentTripDetail.drvDist < 1 ? (
            <span className="trip_info_item">{t('T20341')}</span>
          ) : (
            <span className="trip_info_item">
              {formatNumberWithCommas(convertDistanceValue(formattedRecentTripDetail.drvDist))}
              {convertDistanceSymbol(formattedRecentTripDetail.drvDist)}
            </span>
          )}
        </div>
      </div>
      <ul className="statistics_record_list">
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('T10204')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">{formattedRecentTripDetail.idleTime}</span>
            <span className="statistics_record_unit">{t('T10055')}</span>
          </span>
        </li>
        <div className="divider divider_vertical"></div>
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('T10205')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">
              {formatNumberWithCommas(convertSpeedValue(formattedRecentTripDetail.avgSpeed))}
            </span>
            <span className="statistics_record_unit">{convertSpeedSymbol(formattedRecentTripDetail.avgSpeed)}</span>
          </span>
        </li>
        <div className="divider divider_vertical"></div>
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('T10206')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">
              {formatNumberWithCommas(convertSpeedValue(formattedRecentTripDetail.maxSpeed))}
            </span>
            <span className="statistics_record_unit">{convertSpeedSymbol(formattedRecentTripDetail.maxSpeed)}</span>
          </span>
        </li>
        <div className="divider divider_vertical"></div>
      </ul>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date skeleton">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="trip_date_today">{t('T10008')}</span>
        </div>
        <div className="trip_summary_list type_multi_line type_error skeleton">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10054')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10055')}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10057')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10056')}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        className="trip_info skeleton"
        style={{height: '126px'}}
      >
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('T10008')}</span>}
        </div>
        <div className="trip_summary_list type_multi_line type_error">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10054')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10055')}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10057')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10056')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}
